import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, Link2, Loader2, Sparkles, Download } from "lucide-react";
import { CATEGORIES as ALL_CATEGORIES } from "../../lib/products";

// Categories the admin can pick from (matches product.category stored in Mongo).
// "All" is a filter helper, not a real category, so it's stripped here.
const CATEGORY_OPTIONS = ALL_CATEGORIES
  .filter((c) => c.key !== "All")
  .map((c) => ({
    key: c.key,
    label: c.parent ? `${c.parent} › ${c.label}` : c.label,
  }));

const emptyForm = {
  title: "",
  price: 0,
  category: "Action Figure",
  image: "",
  description: "",
  stock: 0,
  bestseller: false,
  limited: false,
};

const ProductDialog = ({ open, onClose, product, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [processBg, setProcessBg] = useState(true);
  const fileRef = useRef(null);

  useEffect(() => {
    if (product) setForm({ ...emptyForm, ...product });
    else setForm(emptyForm);
    setImportUrl("");
    setProcessBg(true);
  }, [product, open]);

  if (!open) return null;

  const onChange = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("process_bg", processBg ? "true" : "false");
      const { data } = await api.post("/admin/products/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((s) => ({ ...s, image: data.url }));
      toast.success(processBg ? "Immagine caricata (sfondo nero AI)" : "Immagine caricata (originale)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fallito");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onImportUrl = async () => {
    if (!importUrl.trim()) return;
    setUploading(true);
    try {
      const { data } = await api.post("/admin/products/import-image", {
        url: importUrl.trim(),
        name: form.title || undefined,
        process_bg: processBg,
      });
      setForm((s) => ({ ...s, image: data.url }));
      setImportUrl("");
      toast.success("Immagine importata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fallito");
    } finally {
      setUploading(false);
    }
  };

  const onReprocess = async () => {
    if (!product?.id) return;
    setUploading(true);
    try {
      const { data } = await api.post(`/admin/products/${product.id}/reprocess-image`);
      // Append cache-busting query so the preview reloads even at the same URL
      const fresh = `${data.url}${data.url.includes("?") ? "&" : "?"}t=${Date.now()}`;
      setForm((s) => ({ ...s, image: fresh }));
      toast.success("Immagine ri-processata");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ri-processo fallito");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock, 10),
      };
      if (product) {
        await api.put(`/admin/products/${product.id}`, payload);
        toast.success("Prodotto aggiornato");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Prodotto creato");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-black/80 backdrop-blur-md" />
      <div data-testid="product-dialog" className="relative bg-zinc-950 border-4 border-yellow-400 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b-2 border-zinc-800 sticky top-0 bg-zinc-950 z-10">
          <h3 className="font-heading text-2xl uppercase text-white">{product ? "Modifica" : "Nuovo"} Prodotto</h3>
          <button onClick={onClose} className="p-2 text-white hover:bg-yellow-400 hover:text-black border-2 border-zinc-800">
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Titolo</label>
            <input data-testid="product-title" required value={form.title} onChange={onChange("title")}
              className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Prezzo €</label>
              <input data-testid="product-price" type="number" step="0.01" required value={form.price} onChange={onChange("price")}
                className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none" />
            </div>
            <div>
              <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Stock</label>
              <input data-testid="product-stock" type="number" required value={form.stock} onChange={onChange("stock")}
                className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Categoria</label>
            <select data-testid="product-category" value={form.category} onChange={onChange("category")}
              className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none">
              {CATEGORY_OPTIONS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Immagine</label>

            {/* Toggle: applica sfondo nero AI */}
            <button
              type="button"
              role="switch"
              aria-checked={processBg}
              data-testid="toggle-process-bg"
              onClick={() => setProcessBg((v) => !v)}
              className={`w-full mb-2 flex items-center justify-between gap-3 border-2 px-3 py-2.5 transition ${
                processBg
                  ? "bg-yellow-400/10 border-yellow-400 text-yellow-400"
                  : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-600"
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="w-4 h-4 flex-shrink-0" strokeWidth={3} />
                <div className="text-left min-w-0">
                  <div className="font-heading uppercase text-xs tracking-widest">
                    Sfondo nero automatico (AI)
                  </div>
                  <div className="text-[10px] opacity-70 mt-0.5">
                    {processBg
                      ? "Attivo: rimuove sfondo + applica nero"
                      : "Disattivo: mantiene immagine originale"}
                  </div>
                </div>
              </div>
              <div
                className={`w-10 h-5 flex-shrink-0 border-2 relative transition ${
                  processBg ? "bg-yellow-400 border-yellow-400" : "bg-zinc-800 border-zinc-700"
                }`}
              >
                <div
                  className={`absolute top-0.5 w-3 h-3 bg-black transition-all ${
                    processBg ? "left-[calc(100%-14px)]" : "left-0.5"
                  }`}
                />
              </div>
            </button>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
              {/* Upload from disk */}
              <button
                type="button"
                data-testid="upload-image-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex items-center justify-center gap-2 bg-yellow-400 text-black font-heading uppercase tracking-widest text-xs py-2.5 hover:bg-white transition disabled:opacity-40"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={3} />}
                Carica da PC
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={onFilePick}
                data-testid="upload-image-input"
              />

              {/* Import from URL */}
              <div className="flex gap-2">
                <input
                  type="url"
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  placeholder="Incolla URL esterno…"
                  data-testid="import-image-url"
                  className="flex-1 bg-zinc-900 border-2 border-zinc-800 px-3 py-2 text-white text-xs font-mono focus:border-yellow-400 focus:outline-none"
                />
                <button
                  type="button"
                  data-testid="import-image-btn"
                  onClick={onImportUrl}
                  disabled={uploading || !importUrl.trim()}
                  className="bg-black border-2 border-yellow-400 text-yellow-400 px-3 hover:bg-yellow-400 hover:text-black transition disabled:opacity-40"
                  title="Importa immagine dall'URL"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" strokeWidth={3} />}
                </button>
              </div>
            </div>

            <input data-testid="product-image" required value={form.image} onChange={onChange("image")}
              placeholder="https://… (auto-compilato dopo upload)"
              className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none font-mono text-xs" />
            {form.image && (
              <div className="mt-2 flex items-start gap-3">
                <img src={form.image} alt="preview" className="w-32 h-32 object-cover border-2 border-zinc-800 bg-black" />
                {product?.id && (
                  <button
                    type="button"
                    data-testid="reprocess-image-btn"
                    onClick={onReprocess}
                    disabled={uploading}
                    title="Rimuove di nuovo lo sfondo con AI e applica nero assoluto"
                    className="flex items-center gap-2 bg-black border-2 border-yellow-400 text-yellow-400 px-3 py-2 hover:bg-yellow-400 hover:text-black font-heading uppercase tracking-widest text-xs transition disabled:opacity-40"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={3} />}
                    Ri-processa immagine
                  </button>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Descrizione</label>
            <textarea data-testid="product-description" required value={form.description} onChange={onChange("description")} rows={3}
              className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-2 text-white focus:border-yellow-400 focus:outline-none" />
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-zinc-300 cursor-pointer">
              <input data-testid="product-bestseller" type="checkbox" checked={form.bestseller} onChange={onChange("bestseller")}
                className="w-5 h-5 accent-yellow-400" />
              <span className="font-heading uppercase text-sm tracking-widest">Bestseller</span>
            </label>
            <label className="flex items-center gap-2 text-zinc-300 cursor-pointer">
              <input data-testid="product-limited" type="checkbox" checked={form.limited} onChange={onChange("limited")}
                className="w-5 h-5 accent-yellow-400" />
              <span className="font-heading uppercase text-sm tracking-widest">Limited</span>
            </label>
          </div>
          <button type="submit" data-testid="save-product" disabled={saving}
            className="w-full bg-yellow-400 text-black font-heading uppercase py-3 hover:bg-white transition disabled:opacity-40">
            {saving ? "Salvataggio..." : product ? "Aggiorna" : "Crea Prodotto"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      setProducts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!window.confirm(`Eliminare "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Prodotto eliminato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/products/bulk-import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template_prodotti_daitanjack.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template scaricato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore download template");
    }
  };

  return (
    <div data-testid="admin-products" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="font-jp text-yellow-400 text-xs tracking-widest mb-2">商品管理</div>
          <h1 className="font-heading text-5xl uppercase text-white leading-none">Prodotti</h1>
        </div>
        <button
          data-testid="new-product-button"
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="bg-yellow-400 text-black font-heading uppercase px-6 py-3 hover:bg-white transition flex items-center gap-2"
        >
          <Plus className="w-5 h-5" strokeWidth={3} /> Nuovo Prodotto
        </button>
      </div>

      <div className="flex justify-end -mt-2 gap-3">
        <button data-testid="download-template-button" onClick={downloadTemplate}
          className="text-zinc-400 hover:text-yellow-400 text-xs uppercase tracking-widest flex items-center gap-2 border border-zinc-800 hover:border-yellow-400 px-3 py-2 transition">
          <Download className="w-3.5 h-3.5" strokeWidth={3} /> Scarica template import
        </button>
      </div>

      <div className="bg-zinc-900 border-2 border-zinc-800 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-950 border-b-2 border-zinc-800">
            <tr className="text-left">
              {["Immagine", "Titolo", "Categoria", "Prezzo", "Stock", "Flag", "Azioni"].map((h) => (
                <th key={h} className="font-heading uppercase text-yellow-400 text-xs tracking-widest p-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-zinc-500 uppercase">Caricamento...</td></tr>}
            {!loading && products.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-zinc-500 uppercase">Nessun prodotto</td></tr>}
            {products.map((p) => (
              <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-b border-zinc-800 hover:bg-zinc-950/50">
                <td className="p-3"><img src={p.image} alt={p.title} className="w-14 h-14 object-cover border border-zinc-700" /></td>
                <td className="p-3"><div className="text-white font-bold">{p.title}</div><div className="text-zinc-500 text-xs">#{p.id}</div></td>
                <td className="p-3 text-zinc-300 text-sm">{p.category}</td>
                <td className="p-3 text-yellow-400 font-bold">€ {p.price.toFixed(2)}</td>
                <td className="p-3"><span className={`font-bold ${p.stock <= 3 ? "text-red-400" : "text-white"}`}>{p.stock}</span></td>
                <td className="p-3">
                  <div className="flex gap-1 flex-wrap">
                    {p.bestseller && <span className="bg-yellow-400 text-black px-2 py-0.5 text-[10px] font-heading uppercase">Best</span>}
                    {p.limited && <span className="bg-black text-yellow-400 border border-yellow-400 px-2 py-0.5 text-[10px] font-heading uppercase">Lim</span>}
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <button data-testid={`edit-product-${p.id}`} onClick={() => { setEditing(p); setDialogOpen(true); }}
                      className="bg-zinc-800 text-yellow-400 p-2 hover:bg-yellow-400 hover:text-black transition" aria-label="Modifica">
                      <Pencil className="w-4 h-4" strokeWidth={3} />
                    </button>
                    <button data-testid={`delete-product-${p.id}`} onClick={() => del(p)}
                      className="bg-zinc-800 text-red-400 p-2 hover:bg-red-600 hover:text-white transition" aria-label="Elimina">
                      <Trash2 className="w-4 h-4" strokeWidth={3} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ProductDialog open={dialogOpen} onClose={() => setDialogOpen(false)} product={editing} onSaved={load} />
    </div>
  );
}
